<template>
    <div class="relative">
        <select-c
            v-model="currentSelection"
            label="Period:"
            :options="options"
        ></select-c>
    </div>
</template>

<script setup>
import {ref, watch, onMounted, inject, toRefs, nextTick } from 'vue';
import {useLocationHash} from '../../mixins/LocationHash';

// Props
const props = defineProps({
    defaultFields: [String, Array],
    timeframes: Array,
    modelValue: {
        type: [String, Object],
        default: null, // You can set a default value if necessary
    },
});

const emit = defineEmits();
const { hashExists, searchHash, addToHash } = useLocationHash();
const eventBus = inject('eventBus');

const dontConvert = ['contractName', 'contractMonthYear', 'symbol', 'symbolName', 'category', 'uscCategory', 'fullNameWithoutExchange', 'strikePrice', 'strike', 'symbolType', 'longSymbol', 'optionType'];
const suppressUpdate = ref(false);

const { defaultFields, timeframes } = toRefs(props);

// Data
const currentSelection = ref('intraday');
const fields = ref([]);
const options = ref([]);

const convertToEndOfDay = (field) => {
    return `daily${field[0].toUpperCase()}${field.slice(1)}`;
};

const convertToIntraday = (field) => {
    const fieldName = field.replace(/^daily/, '');
    return `${fieldName[0].toLowerCase()}${fieldName.slice(1)}`;
};

const shouldConvert = (field) => {
    return !dontConvert.includes(field);
};

const convertField = (field) => {
    if (!shouldConvert(field)) return field;

    if (currentSelection.value === 'intraday') {
        return convertToIntraday(field);
    }

    if (currentSelection.value === 'eod') {
        return convertToEndOfDay(field);
    }
};

function isDefault(timeframeValue) {
    if (hashExists()) {
        const hash = searchHash('timeframe');
        return hash && hash.value === timeframeValue;
    }
}

const update = () => {
    const updatedFields = fields.value.map((mField) => {
        return {
            new: convertField(mField),
            old: mField
        };
    });

    fields.value = updatedFields.map((mField) => mField.new);

    addToHash('timeframe', currentSelection.value);
    eventBus.emit('timeframeChanged', updatedFields);
};

watch(defaultFields, (newFields) => {
    if (typeof newFields === 'string') {
        fields.value = newFields.split(',');
    } else if (Array.isArray(newFields)) {
        fields.value = newFields;
    } else {
        console.error('Invalid defaultFields provided');
    }
});

watch(timeframes, (newTimeframes) => {
    if (newTimeframes) {
        options.value = newTimeframes;

        suppressUpdate.value = true;

        setTimeout(() => {
            suppressUpdate.value = false;
        }, 250);
    }
});

watch(() => props.modelValue, (newValue) => {
    currentSelection.value = newValue;
    emit('update:modelValue', newValue);
    addToHash('timeframe', newValue);
});

// Emit event on selection change
watch(currentSelection, (newValue) => {
    if (suppressUpdate.value) return;
    currentSelection.value = newValue;
    emit('update:modelValue', newValue);
    update();
});

onMounted(() => {
    // Initialize fields and options if needed
    if (typeof props.defaultFields === 'string') {
        fields.value = props.defaultFields.split(',');
    } else if (Array.isArray(props.defaultFields)) {
        fields.value = props.defaultFields;
    }

    options.value = props.timeframes || [
        { label: 'Intraday', value: 'intraday', default: isDefault('intraday') },
        { label: 'End-of-Day', value: 'eod', default: isDefault('eod') }
    ];

    if (hashExists()) {
        const hash = searchHash('timeframe');
        if (hash && hash.value) {
            currentSelection.value = hash.value;
        };
    }

    // Emit initial selection
    emit('update:modelValue', currentSelection.value);
});

</script>
